import { Injectable, computed, effect, signal } from '@angular/core';
import { DetachedRouteHandle } from '@angular/router';
import { Subject, Observable } from 'rxjs';

export interface TabItem {
  route: string;
  title: string;
  tabType?: string;
  mode?: string;
}

/** Survives a closed browser tab (unlike sessionStorage) — restored tabs only carry route/title/
 *  type/mode; the actual component instance is always recreated fresh on first visit, same as
 *  clicking a never-opened tab. */
const TABS_STORAGE_KEY = 'kl_open_tabs';

function isTabItem(value: object): value is TabItem {
  return (
    'route' in value &&
    'title' in value &&
    typeof (value as TabItem).route === 'string' &&
    typeof (value as TabItem).title === 'string'
  );
}

function readStoredTabs(): TabItem[] {
  try {
    const raw = localStorage.getItem(TABS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isTabItem) : [];
  } catch {
    return [];
  }
}

@Injectable({ providedIn: 'root' })
export class TabService {
  private readonly _tabs = signal<TabItem[]>(readStoredTabs());
  private readonly _currentRoute = signal<string>('');
  private readonly handles = new Map<string, DetachedRouteHandle>();
  private readonly suppressStoreKeys = new Set<string>();

  private readonly _tabRemovedSource = new Subject<string>();
  readonly tabRemoved$: Observable<string> = this._tabRemovedSource.asObservable();

  private readonly _gridRefreshSource = new Subject<string>();
  readonly gridRefresh$: Observable<string> = this._gridRefreshSource.asObservable();

  readonly tabs = this._tabs.asReadonly();
  readonly currentRoute = this._currentRoute.asReadonly();

  readonly currentTab = computed(() => this._tabs().find((t) => t.route === this._currentRoute()) ?? null);

  constructor() {
    effect(() => {
      const tabs = this._tabs();
      try {
        localStorage.setItem(TABS_STORAGE_KEY, JSON.stringify(tabs));
      } catch {
        // storage unavailable/full — tabs keep working in-memory for this session
      }
    });
  }

  /**
   * Opens or focuses a tab. Called on every NavigationEnd.
   */
  openTab(route: string, data?: Record<string, any>): void {
    this._currentRoute.set(route);

    const existing = this._tabs().find((t) => t.route === route);
    if (!existing) {
      const qp = (data?.['safeQueryParams'] ?? {}) as Record<string, string | undefined>;
      const modeRaw = qp['mode'];

      const tab: TabItem = {
        route,
        title: data?.['tabTitle'] ?? this.inferTitle(route),
        tabType: data?.['tabType'],
        mode: modeRaw,
      };
      this._tabs.update((tabs) => [...tabs, tab]);
    }
  }

  /**
   * Removes a tab by route. Returns the route to navigate to, or null.
   */
  closeTab(route: string): string | null {
    const tabs = this._tabs();
    const idx = tabs.findIndex((t) => t.route === route);
    if (idx === -1) return null;

    // Sole home/start tab cannot be closed
    if (tabs.length <= 1 && route.split('?')[0] === '/dashboard') return null;

    const isCurrent = this._currentRoute() === route;
    this._tabs.update((ts) => ts.filter((t) => t.route !== route));
    this.handles.delete(route);
    if (isCurrent) {
      this.suppressStoreKeys.add(route);
    }
    this._tabRemovedSource.next(route);

    if (isCurrent) {
      const remaining = this._tabs();
      const next = remaining[Math.min(idx, remaining.length - 1)];
      return next?.route ?? '/dashboard';
    }
    return null;
  }

  closeTabsToRight(route: string): void {
    const tabs = this._tabs();
    const idx = tabs.findIndex((t) => t.route === route);
    if (idx === -1) return;
    const removed = tabs.slice(idx + 1);
    this._tabs.update((ts) => ts.slice(0, idx + 1));
    removed.forEach((t) => {
      this.handles.delete(t.route);
      this._tabRemovedSource.next(t.route);
    });
  }

  closeTabsToLeft(route: string): void {
    const tabs = this._tabs();
    const idx = tabs.findIndex((t) => t.route === route);
    if (idx === -1) return;
    const removed = tabs.slice(0, idx);
    this._tabs.update((ts) => ts.slice(idx));
    removed.forEach((t) => {
      this.handles.delete(t.route);
      this._tabRemovedSource.next(t.route);
    });
  }

  closeOtherTabs(route: string): void {
    const tabs = this._tabs();
    const kept = tabs.find((t) => t.route === route);
    if (!kept) return;
    const removed = tabs.filter((t) => t.route !== route);
    this._tabs.set([kept]);
    removed.forEach((t) => {
      this.handles.delete(t.route);
      this._tabRemovedSource.next(t.route);
    });
  }

  refreshGrid(route: string): void {
    this._gridRefreshSource.next(route);
  }

  reset(): void {
    this._tabs.set([]);
    this._currentRoute.set('');
    this.handles.clear();
    try {
      localStorage.removeItem(TABS_STORAGE_KEY);
    } catch {
      // storage unavailable — nothing to clean up
    }
  }

  setHandle(route: string, handle: DetachedRouteHandle): void {
    this.handles.set(route, handle);
  }

  getHandle(route: string): DetachedRouteHandle | undefined {
    return this.handles.get(route);
  }

  hasHandle(route: string): boolean {
    return this.handles.has(route);
  }

  /** Whether `route` is still represented by an open tab (not just closed via `closeTab`). */
  hasTab(route: string): boolean {
    return this._tabs().some((t) => t.route === route);
  }

  removeHandle(route: string): void {
    this.handles.delete(route);
  }

  consumeSuppressStore(route: string): boolean {
    return this.suppressStoreKeys.delete(route);
  }

  private inferTitle(route: string): string {
    const segments = route.split('/').filter(Boolean);
    const last = segments[segments.length - 1] ?? 'dashboard';
    return last
      .replace(/([A-Z])/g, ' $1')
      .replace(/[-_]/g, ' ')
      .trim();
  }
}
